import { useState, useEffect } from 'react';
import { getHealthItems, addHealthItem, toggleHealthItem, deleteHealthItem, aiHealthCheck } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, Heart, Pill, Stethoscope, Syringe, AlertCircle, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CAT_ICONS = { medication: Pill, travel_kit: Stethoscope, vaccine: Syringe, allergy: AlertCircle };
const CAT_LABELS = { medication: 'Medicaments', travel_kit: 'Trousse de voyage', vaccine: 'Vaccins', allergy: 'Allergies' };
const CAT_COLORS = { medication: '#3A86FF', travel_kit: '#00CC99', vaccine: '#8B5CF6', allergy: '#F43F5E' };

export const HealthModule = ({ tripId, trip }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: 'medication', title: '', notes: '', reminder_date: '' });
  const [aiChecking, setAiChecking] = useState(false);

  const load = () => { getHealthItems(tripId).then(r => setItems(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, [tripId]); // eslint-disable-line

  const handleAdd = async () => {
    if (!form.title) return;
    await addHealthItem(tripId, form);
    setForm({ category: 'medication', title: '', notes: '', reminder_date: '' });
    setOpen(false); toast.success('Ajoute !'); load();
  };

  const handleAiCheck = async () => {
    setAiChecking(true);
    try {
      const res = await aiHealthCheck(tripId);
      toast.success(`${res.data.generated_count} recommandations sante generees`);
      load();
    } catch (e) { toast.error('Erreur: ' + (e.response?.data?.detail || 'Echec')); }
    setAiChecking(false);
  };

  const handleToggle = async (id) => { await toggleHealthItem(id); load(); };
  const handleDelete = async (id) => { await deleteHealthItem(id); load(); };

  const categories = ['medication', 'travel_kit', 'vaccine', 'allergy'];
  const checked = items.filter(i => i.is_checked).length;

  return (
    <div className="space-y-4" data-testid="health-module">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FFF1F2] flex items-center justify-center">
            <Heart className="w-5 h-5 text-[#F43F5E]" strokeWidth={1.8} />
          </div>
          <div>
            <h3 className="text-base font-bold text-[#1E293B]">Sante & Confort</h3>
            <p className="text-xs text-[#94A3B8]">{checked}/{items.length} elements verifies</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleAiCheck} disabled={aiChecking}
            className="rounded-xl bg-gradient-to-r from-[#3A86FF] to-[#7C3AED] text-white text-xs h-9 px-4" data-testid="ai-health-check-btn">
            {aiChecking ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Analyse sante...</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Verifier par IA</>}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-[#3A86FF] hover:bg-[#2563EB] text-white text-xs h-9" data-testid="add-health-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Ajouter un element sante</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <div>
                <Label className="text-sm">Categorie</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c} value={c}>{CAT_LABELS[c]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Titre</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 h-11" placeholder="Doliprane enfant" data-testid="health-title" />
              </div>
              <div>
                <Label className="text-sm">Notes</Label>
                <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1 h-11" placeholder="Posologie..." />
              </div>
              <Button onClick={handleAdd} className="w-full rounded-xl bg-[#3A86FF] hover:bg-[#2563EB] text-white" data-testid="save-health-btn">Ajouter</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {categories.map(cat => {
        const catItems = items.filter(i => i.category === cat);
        if (catItems.length === 0) return null;
        const Icon = CAT_ICONS[cat] || Heart;
        return (
          <div key={cat}>
            <div className="flex items-center gap-2 mb-2">
              <Icon className="w-4 h-4" style={{ color: CAT_COLORS[cat] }} strokeWidth={1.8} />
              <span className="text-sm font-semibold text-[#1E293B]">{CAT_LABELS[cat]}</span>
              <Badge variant="secondary" className="text-[10px] rounded-full">{catItems.length}</Badge>
            </div>
            <div className="space-y-1.5 ml-6">
              {catItems.map(item => (
                <div key={item.item_id} className="widget-card p-3 flex items-center gap-3 group" data-testid={`health-${item.item_id}`}>
                  <Checkbox checked={item.is_checked} onCheckedChange={() => handleToggle(item.item_id)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className={`text-sm ${item.is_checked ? 'line-through text-[#94A3B8]' : 'text-[#1E293B]'}`}>{item.title}</p>
                      {item.ai_generated && <Badge className="text-[8px] bg-[#EBF2FF] text-[#3A86FF] border-0 rounded-md px-1.5">IA</Badge>}
                      {item.priority && <Badge className={`text-[8px] border-0 rounded-md px-1.5 ${item.priority === 'obligatoire' ? 'bg-red-50 text-red-600' : item.priority === 'recommande' ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-blue-600'}`}>{item.priority}</Badge>}
                    </div>
                    {item.notes && <p className="text-[10px] text-[#94A3B8] leading-relaxed mt-0.5">{item.notes}</p>}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(item.item_id)} className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-red-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {items.length === 0 && (
        <div className="widget-card p-10 text-center">
          <Heart className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" strokeWidth={1.2} />
          <p className="text-sm text-[#94A3B8]">Ajoutez vos medicaments, trousse de voyage et vaccins</p>
        </div>
      )}
    </div>
  );
};
